import { msg, Trans } from '@lingui/macro'

import * as React from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { useTrans } from '~logic/i18n/useTrans'
import { AnimatedHeight } from '~ui/animatedHeight/AnimatedHeight'
import { InputControl } from '~ui/components/formElements/accessories/InputControl'
import { InputLabel } from '~ui/components/formElements/accessories/InputLabel'
import { InputErrorMessage } from '~ui/components/formElements/accessories/InputMessages'
import { Input } from '~ui/components/formElements/Input/Input'

import { FORM_INPUT_RADIUS } from '../config/form.config'

type TCodeForm = {
  phonePrefix: string
  phoneNumber: string
  code: string
}

type TProps = NoChildren

/**
 * Input for one-time security code.
 *
 * ! Needs the `FormProvider` from 'react-hook-form' to be rendered somewhere above,
 * see {@link https://react-hook-form.com/api/formprovider} for examples and details.
 */
export const SecurityCodeInput: React.FC<TProps> = () => {
  const { trans } = useTrans()
  const { control } = useFormContext<TCodeForm>()

  return (
    <InputControl isRequired>
      <InputLabel>
        <Trans>Enter the security code from SMS</Trans>
      </InputLabel>
      <Controller
        name={`code`}
        control={control}
        rules={{
          required: {
            value: true,
            message: trans(msg`Fill the security code`),
          },
        }}
        render={({
          field: { onChange, onBlur, value, name },
          fieldState: { invalid, error },
          formState: { isSubmitting },
        }) => (
          <>
            <Input
              id={name}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
              isInvalid={invalid}
              inputMode={`numeric`}
              autoComplete={`one-time-code`}
              isDisabled={isSubmitting}
              radius={FORM_INPUT_RADIUS}
            />
            <AnimatedHeight isOpen={invalid && Boolean(error?.message)}>
              <InputErrorMessage>{error?.message ?? null}</InputErrorMessage>
            </AnimatedHeight>
          </>
        )}
      />
    </InputControl>
  )
}
